'use client';
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import * as signalR from '@microsoft/signalr';
import store from '../store';
import { useAuthContext } from './AuthContext';
import {
  handelShowLastMessage,
  renderSentMessage,
} from 'features/chat/utils/renderSentMessage';

import { toast } from 'react-toastify';
import MessageNotification from 'features/chat/components/MessageNotification';
import { useMediaContext } from './MediaContext';
import { fetchPermissionsApiCall } from 'features/permissions/services/permissionService';
import { updatePermissions } from 'features/auth/slices/authSlice';
import { UpdateSkin } from './SkinProvider';
import { getClientFromUrl } from 'utils/axiosInstance';
import { handleNetworkError, isNetworkError } from 'utils/networkConnectivity';

type SignalRContextType = {
  connection: signalR.HubConnection | null;
  isConnected: boolean;
};

const SignalRContext = createContext<SignalRContextType>({
  connection: null,
  isConnected: false,
});

export const useSignalR = () => useContext(SignalRContext);

type Props = {
  children: ReactNode;
};

export const SignalRProvider = ({ children }: Props) => {
  const [connection, setConnection] = useState<signalR.HubConnection | null>(
    null,
  );
  const [isConnected, setIsConnected] = useState(false);
  const { openMedia } = useMediaContext();
  const { user } = useAuthContext();

  useEffect(() => {
    if (!user || !user.userId) return;

    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.NEXT_PUBLIC_API_BASE_URL}realtimeHub`, {
        // Read at each (re)connect: the JWT changes at every refresh.
        accessTokenFactory: () => store.getState().auth?.token ?? '',
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .build();

    setConnection(newConnection);

    newConnection
      .start()
      .then(() => {
        setIsConnected(true);
      })
      .catch(async (err) => {
        // A remount/auth re-render can stop the connection while it is still negotiating —
        // withAutomaticReconnect recovers on its own, so that race is expected, not an error.
        const aborted = /before stop\(\)|stopped during negotiation/i.test(
          err instanceof Error ? err.message : String(err),
        );
        if (aborted) return;
        console.error('❌ SignalR Connection Error:', err);
        if (isNetworkError(err)) {
          await handleNetworkError();
        }
      });

    return () => {
      newConnection
        .stop()
        .then(() => {
          setIsConnected(false);
        })
        .catch(async (err) => {
          console.error('❌ Error stopping SignalR:', err);
          if (isNetworkError(err)) {
            await handleNetworkError();
          }
        });
    };
    // The connection's lifetime follows the IDENTITY (userId), not the user object reference —
    // auth-state re-renders were tearing down a socket still negotiating and reconnecting it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  useEffect(() => {
    if (isConnected) {
      connection?.on(
        'updatedTheme',
        (res: { subDomain: string; theme: string }) => {
          const client = getClientFromUrl();
          if (client == res.subDomain) {
            document.cookie = `theme=${res.theme.toLowerCase() ?? 'MintRH'}; path=/; max-age=31536000`;
            UpdateSkin(res.theme.toLowerCase());
          }
        },
      );
      connection?.on('updatedUserRole', (userId, message, files) => {
        const handelRefreshPermission = async () => {
          const permissions = await fetchPermissionsApiCall('test');
          store.dispatch(updatePermissions(permissions));
          window.location.reload();
        };
        handelRefreshPermission();
      });
      connection?.on('ReceiveMessage', (userId, message, files) => {

        if (user.userId == userId) return;
        const container = document.querySelector('.minthr-chat-messages');
        const userCard = document.querySelector(
          `.minthr-chat-user-item[data-id="${userId}"]`,
        );
        if (container || userCard) {
          const resiverId = container?.getAttribute('data-id');
          if (resiverId && resiverId == userId) {
            renderSentMessage({ item: files, user: user, openMedia });
          }

          handelShowLastMessage(
            userCard as HTMLDivElement,
            message,
            files.isgroup,
          );
        } else {
          toast(<MessageNotification userId={userId} message={files} />, {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
            className:
              'minthr-box-shadow  minthr-bg-white minthr-chat-notification',
          });
        }
      });
    }
  }, [isConnected]);

  return (
    <SignalRContext.Provider value={{ connection, isConnected }}>
      {children}
    </SignalRContext.Provider>
  );
};
